import React, { useState, useEffect } from 'react';
import { loadsAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Truck, MapPin, Clock, CheckCircle, ArrowRight, Play, Compass } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const statusColors = {
  draft: 'bg-slate-100 text-slate-800',
  planned: 'status-pending',
  dispatched: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400',
  en_route: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  arrived: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  delivering: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  completed: 'status-success'
};

const DispatchPage = () => {
  const [loads, setLoads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedLoad, setSelectedLoad] = useState(null);

  useEffect(() => {
    fetchLoads();
  }, [statusFilter]);

  const fetchLoads = async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const response = await loadsAPI.list(params);
      setLoads(response.data);
    } catch (error) {
      toast.error('Failed to load dispatch data');
    } finally {
      setLoading(false);
    }
  };

  const handleDispatch = async (loadId) => {
    try {
      await loadsAPI.dispatch(loadId);
      toast.success('Load dispatched to driver');
      fetchLoads();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dispatch load');
    }
  };

  const handleUpdateStatus = async (loadId, status) => {
    try {
      await loadsAPI.update(loadId, { status });
      toast.success('Load status updated');
      fetchLoads();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getProgress = (load) => {
    if (!load.stops || load.stops.length === 0) return 0;
    const completed = load.stops.filter(s => s.status === 'completed').length;
    return Math.round((completed / load.stops.length) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="dispatch-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-heading font-bold">Dispatch Console</h1>
          <p className="text-muted-foreground">Monitor and manage active deliveries</p>
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Statuses</SelectItem>
            <SelectItem value="planned">Planned</SelectItem>
            <SelectItem value="dispatched">Dispatched</SelectItem>
            <SelectItem value="en_route">En Route</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{loads.filter(l => l.status === 'planned').length}</p>
                <p className="text-xs text-muted-foreground">Pending Dispatch</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                <Compass className="w-5 h-5 text-cyan-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{loads.filter(l => ['dispatched', 'en_route'].includes(l.status)).length}</p>
                <p className="text-xs text-muted-foreground">In Transit</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <Play className="w-5 h-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{loads.filter(l => l.status === 'delivering').length}</p>
                <p className="text-xs text-muted-foreground">Delivering</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{loads.filter(l => l.status === 'completed').length}</p>
                <p className="text-xs text-muted-foreground">Completed Today</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Loads List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
        {loads.length === 0 ? (
          <div className="col-span-full text-center py-12 text-muted-foreground">
            No loads found
          </div>
        ) : (
          loads.map((load) => (
            <Card key={load.id} className="hover:border-amber-500/50 transition-colors" data-testid={`dispatch-load-${load.id}`}>
              <CardHeader className="pb-2">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base font-mono">{load.load_number}</CardTitle>
                    <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                      <Truck className="w-4 h-4" />
                      <span>{load.vehicle_registration || 'N/A'}</span>
                    </div>
                  </div>
                  <Badge className={statusColors[load.status] || 'bg-slate-100'}>
                    {load.status?.replace('_', ' ')}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Driver</span>
                    <span>{load.driver_name || 'Not assigned'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Stops</span>
                    <span>{load.stop_count || load.stops?.length || 0} deliveries</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Volume</span>
                    <span className="font-mono">{(load.total_volume || 0).toLocaleString()} L</span>
                  </div>

                  {/* Progress bar for active loads */}
                  {['dispatched', 'en_route', 'delivering'].includes(load.status) && (
                    <div className="space-y-1">
                      <div className="flex justify-between text-xs text-muted-foreground">
                        <span>Progress</span>
                        <span>{getProgress(load)}%</span>
                      </div>
                      <Progress value={getProgress(load)} className="h-2" />
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="pt-2">
                    {load.status === 'planned' || load.status === 'draft' ? (
                      <Button
                        className="w-full bg-amber-500 hover:bg-amber-400 text-slate-900 font-bold"
                        onClick={() => handleDispatch(load.id)}
                        data-testid={`dispatch-btn-${load.id}`}
                      >
                        <ArrowRight className="w-4 h-4 mr-2" />
                        Dispatch Now
                      </Button>
                    ) : load.status === 'dispatched' ? (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => handleUpdateStatus(load.id, 'en_route')}
                      >
                        Mark En Route
                      </Button>
                    ) : null}
                  </div>

                  {/* Scheduled time */}
                  {load.scheduled_date && (
                    <p className="text-xs text-muted-foreground flex items-center gap-1 pt-2 border-t">
                      <Clock className="w-3 h-3" />
                      Scheduled: {format(new Date(load.scheduled_date), 'dd/MM/yy HH:mm')}
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </div>
  );
};

export default DispatchPage;
